// Featured guides behind the website's city pages: fixes for problems found
// while writing the pages (closed sights, duplicates, wrong pins, wrong
// Wikipedia notes). Built offline from the reviewed proposals
// (Featured Videos/guide-fixes-batch1-….json):
//   { fixes: [{ guide_info_id, guide, city, n, issue, change, type, person?, ops: [...] }] }
// A fix with no ops ("person") needs a person in the app and is only shown.
// ops, all on IDs from the guide as it was reviewed:
//   { op: "remove", event_id, place_id }            stop taken out of a plan
//   { op: "repoint", event_id, from, to, subject }  stop now visits another place
//   { op: "set", place_id, set: {...}, expect: {...} }
//   { op: "note", place_id, text }                   note line at the top of the notes
//   { op: "add_place", place: {...} }                new place (for a replacement)
//   { op: "plan", itinerary, expect: [...], events: [...] }
//       a whole plan's stops and times replaced (2026-10-28, Osaka/Cairo);
//       expect = planSignature of the plan as reviewed
//   { op: "cover", image, image_m, expect: { image, image_m } }
//       the guide's cover (2026-10-30). Set in the guide's attributes; the
//       panel also writes it to guide_info, which the Library shows (every
//       save copies attributes over guide_info, so both must change)
//   { op: "rename_guide", name, expect: { guide_name } }
//       the guide's name (2026-10-30, guides shortened to the days their city can fill); like the
//       cover, set in the attributes and written to guide_info by the panel
//   { op: "create_guide", name, data }
//       a NEW guide, built offline (places, plans, Main, name, cover), created in the signed-in
//       account with the app's own new-guide save; never twice — a guide with that name already
//       loaded counts as done (2026-10-31, new Featured guides without starting them in the app)
//   { op: "itinerary", html }
//       the guide's Home/itinerary page (the Featured guides' cover + "Show Itinerary for:" buttons page),
//       written ONLY over the placeholder text of a new guide — never over a page someone wrote (2026-10-31)
//   { op: "home", html }
//       the guide's whole Home page replaced by the new hub page (2026-11-02, itinerary_page.home_page). Only for the
//       Featured guides, whose Home pages are all generated (the user's decision) — never use it on a person's guide
//   { op: "link", from, to }
//       one link on the guide's Home page pointed elsewhere: every href="from" becomes href="to"
//       (2026-11-01: the Climate button's weatherspark / Google search link → the app's climatelink:<place id>)
//   { op: "share", uid }
//       another account may modify the guide (its uid added to guide_info's access list: bare uid = Modify),
//       so the Featured guides can be looked after from one account (2026-10-31: andiamoplaces@gmail.com)
//   { op: "merge_place", place_id, into, expect: { Name } }
//       a duplicate place folded into its twin (2026-11-03: improvement passes left Featured guides with the same
//       place twice or more, e.g. Florence's Piazzale Michelangelo ×4): its stops visit `into`, links on the Home
//       page to it point to `into`, and the duplicate is deleted
//   { op: "featured" }
//       the guide becomes a Featured guide (guide_info type "Featured" and copying allowed, as the
//       Featured checkbox does) AFTER its fixed content is saved — a new guide started in the app with
//       places only shows up in the Library only once it is complete (2026-10-31)
// A fix is applied only while the guide still looks the way it was reviewed,
// so later edits in the app are never overwritten.
// Used by Admin Dashboard → Guides (FeaturedGuideFixPanel). 2026-10-28.

import { isPlaceholderDoc } from "./exportTripHTML";

const placeOf = (data, id) => (data?.places?.placesList || []).find((p) => String(p.ID) === String(id));
const eventOf = (data, id) => (data?.schedule?.events || []).find((e) => String(e.ID) === String(id));
const firstPlace = (e) => String(e?.Places || "").split(",")[0].trim();
const noteHtml = (text) => `<p><b>Note:</b> ${String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;")}</p>`;
const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const itineraryOf = (e) => (e?.Itinerary == null ? "Main" : e.Itinerary);

// A plan's events reduced to what a "plan" fix checks: ID, place(s), title
// and times, in time order.
export function planSignature(events, itinerary) {
  return (events || [])
    .filter((e) => itineraryOf(e) === itinerary)
    .map((e) => [String(e.ID), String(e.Places || ""), String(e.Subject || ""), e.Start, e.End])
    .sort((a, b) => (a[3] < b[3] ? -1 : a[3] > b[3] ? 1 : a[0] < b[0] ? -1 : 1));
}

// "ready", "done" (already in the guide) or "changed" (edited since the review)
// Can this account change the guide (owner, or Modify access in guide_info.access)?
export const canModify = (guide, uid) =>
  !!uid && !!guide && (guide.owner === uid || (guide.access || []).some((a) => a === uid || a === `${uid}:M` || a === "All_M"));

function opStatus(data, o, guide) {
  if (o.op === "share") return (guide?.access || []).some((a) => a === o.uid || a === `${o.uid}:M`) ? "done" : "ready";
  if (o.op === "itinerary") {
    if ((data?.document || "") === o.html) return "done";
    return isPlaceholderDoc(data?.document) ? "ready" : "changed";
  }
  if (o.op === "home") return (data?.document || "") === o.html ? "done" : "ready";
  if (o.op === "merge_place") {
    const dup = placeOf(data, o.place_id);
    if (!placeOf(data, o.into)) return "changed";
    if (!dup) return "done";
    return dup.Name === o.expect?.Name ? "ready" : "changed";
  }
  if (o.op === "link") {
    const doc = data?.document || "";
    if (doc.includes(`href="${o.from}"`)) return "ready";
    return doc.includes(`href="${o.to}"`) ? "done" : "changed";
  }
  if (o.op === "featured") return guide?.type === "Featured" ? "done" : "ready";
  if (o.op === "remove") {
    const e = eventOf(data, o.event_id);
    if (!e) return "done";
    return firstPlace(e) === String(o.place_id) ? "ready" : "changed";
  }
  if (o.op === "repoint") {
    const e = eventOf(data, o.event_id);
    if (!e) return "changed";
    if (firstPlace(e) === String(o.to)) return "done";
    return firstPlace(e) === String(o.from) ? "ready" : "changed";
  }
  if (o.op === "add_place") return placeOf(data, o.place.ID) ? "done" : "ready";
  if (o.op === "rename_guide") {
    const n = data?.attributes?.guide_name || "";
    if (n === o.name) return "done";
    return n === (o.expect?.guide_name || "") ? "ready" : "changed";
  }
  if (o.op === "cover") {
    const a = data?.attributes || {};
    if (a.image_m === o.image_m) return "done";
    return (a.image_m || "") === (o.expect?.image_m || "") ? "ready" : "changed";
  }
  if (o.op === "plan") {
    const now = planSignature(data?.schedule?.events, o.itinerary);
    if (same(now, planSignature(o.events, o.itinerary))) return "done";
    return same(now, o.expect) ? "ready" : "changed";
  }
  const p = placeOf(data, o.place_id);
  if (!p) return "changed";
  if (o.op === "note") return String(p.Info || "").includes(noteHtml(o.text)) ? "done" : "ready";
  if (Object.entries(o.set).every(([k, v]) => same(p[k], v))) return "done";
  return Object.entries(o.expect || {}).every(([k, v]) => same(p[k], v)) ? "ready" : "changed";
}

// guides: getFeaturedGuidesData's list. One row per fix:
// { fix, guide, status: "ready" | "done" | "changed" | "missing" | "person" }
const createOp = (fix) => (fix?.ops || []).find((o) => o.op === "create_guide");
export const isCreateFix = (fix) => !!createOp(fix);

export function planGuideFixes(guides, fixes) {
  const byInfo = new Map((guides || []).map((g) => [g.guide_info_id, g]));
  return (fixes || []).map((fix) => {
    const c = createOp(fix);
    if (c) {
      // a new guide: done once a guide of that name exists; until then a guide-to-be the panel creates
      const made = (guides || []).find((g) => g.name === c.name || g.data?.attributes?.guide_name === c.name);
      if (made) return { fix, guide: made, status: "done" };
      return { fix, guide: { create: true, guide_info_id: fix.guide_info_id, guide_id: null, name: c.name, owner: null, type: "", data: c.data }, status: "ready" };
    }
    const guide = byInfo.get(fix.guide_info_id);
    if (!guide) return { fix, guide, status: "missing" };
    if (!fix.ops || fix.ops.length === 0) return { fix, guide, status: "person" };
    const st = fix.ops.map((o) => opStatus(guide.data, o, guide));
    if (st.includes("changed")) return { fix, guide, status: "changed" };
    return { fix, guide, status: st.every((s) => s === "done") ? "done" : "ready" };
  });
}

// "Apply to all cities": the ready rows (minus unticked keys "guide_info_id|n"),
// grouped by guide, only guides owned by uid. Returns { byGuide: Map(guide_id →
// rows), count, others } where others = ready fixes in guides owned by someone else.
export function readyByGuide(rows, skip, uid) {
  const byGuide = new Map();
  let count = 0;
  let others = 0;
  (rows || []).forEach((r) => {
    if (r.status !== "ready" || (skip && skip.has(`${r.fix.guide_info_id}|${r.fix.n}`))) return;
    // a guide-to-be is created in (so belongs to) the signed-in account
    if (!r.guide.create && !canModify(r.guide, uid)) {
      others++;
      return;
    }
    const k = r.guide.guide_id || r.guide.guide_info_id;
    byGuide.set(k, [...(byGuide.get(k) || []), r]);
    count++;
  });
  return { byGuide, count, others };
}

// The guide_info fields a list of fixes changes ({ image, image_m } for a cover, guide_name for a
// rename), or null when none does.
export function infoOf(fixes) {
  const out = {};
  (fixes || []).flatMap((f) => f.ops || []).forEach((o) => {
    if (o.op === "cover") Object.assign(out, { image: o.image, image_m: o.image_m });
    if (o.op === "rename_guide") out.guide_name = o.name;
  });
  return Object.keys(out).length ? out : null;
}

// The accounts the fixes share the guide with.
export const shareOf = (fixes) => (fixes || []).flatMap((f) => (f.ops || []).filter((o) => o.op === "share").map((o) => o.uid));
// True when the fixes change the guide's content (not only who may edit it or whether it is Featured).
export const changesData = (fixes) => (fixes || []).some((f) => (f.ops || []).some((o) => !["share", "featured"].includes(o.op)));

// True when the fixes make the guide Featured (the panel does that after saving the content).
export const featuredOf = (fixes) => (fixes || []).some((f) => (f.ops || []).some((o) => o.op === "featured"));

// The guide_info ids a fixes file names that a list of loaded guides doesn't have (guides that aren't
// Featured yet), for the panel to load as well.
export function missingGuideIds(guides, fixes) {
  const have = new Set((guides || []).map((g) => g.guide_info_id));
  return [...new Set((fixes || []).filter((f) => !isCreateFix(f)).map((f) => f.guide_info_id))].filter((id) => id && !have.has(id));
}

// Returns a copy of the guide's data with the fixes applied, in order.
// A renamed place also renames its schedule events that carry the old name.
export function applyGuideFixes(data, fixes) {
  let places = [...(data.places?.placesList || [])];
  let events = [...(data.schedule?.events || [])];
  let attributes = data.attributes;
  let document = data.document;
  const setPlace = (id, change) => {
    places = places.map((p) => (String(p.ID) === String(id) ? change(p) : p));
  };
  (fixes || []).forEach((fix) =>
    (fix.ops || []).forEach((o) => {
      if (o.op === "remove") {
        events = events.filter((e) => !(String(e.ID) === String(o.event_id) && firstPlace(e) === String(o.place_id)));
      } else if (o.op === "repoint") {
        events = events.map((e) =>
          String(e.ID) === String(o.event_id) && firstPlace(e) === String(o.from) ? { ...e, Places: String(o.to), Subject: o.subject } : e
        );
      } else if (o.op === "plan") {
        events = [...events.filter((e) => itineraryOf(e) !== o.itinerary), ...o.events.map((e) => ({ ...e }))];
      } else if (o.op === "add_place") {
        if (!places.some((p) => String(p.ID) === String(o.place.ID))) places = [...places, { ...o.place }];
      } else if (o.op === "cover") {
        attributes = { ...attributes, image: o.image, image_m: o.image_m };
        // the Home page shows the cover too (written into its HTML, & as &amp;): that picture changes with it
        // (2026-11-02, Bagheria's cover showed Aspra on the Home page after the guide's cover was fixed)
        const was = o.expect?.image_m;
        if (was && o.image_m) {
          const esc = (u) => String(u).replace(/&/g, "&amp;").replace(/"/g, "%22");
          document = (document || "").split(`src="${esc(was)}"`).join(`src="${esc(o.image_m)}"`).split(`src="${was}"`).join(`src="${esc(o.image_m)}"`);
        }
      } else if (o.op === "home") {
        document = o.html;
      } else if (o.op === "merge_place") {
        const from = String(o.place_id), to = String(o.into);
        if (places.some((p) => String(p.ID) === to)) {
          events = events.map((e) => {
            const ids = String(e.Places || "").split(",").map((x) => x.trim()).filter(Boolean);
            if (!ids.includes(from)) return e;
            return { ...e, Places: [...new Set(ids.map((x) => (x === from ? to : x)))].join(",") };
          });
          // app links on the Home page (placemenulink:<id>, directionslink:<id>|<id>, …)
          const link = new RegExp(`(href="[a-z]+link:(?:[^"]*?[^0-9"])?)${from}(?![0-9])`);
          let doc = document || "";
          for (let k = 0; k < 200 && link.test(doc); k++) doc = doc.replace(link, `$1${to}`);
          document = doc;
          places = places.filter((p) => String(p.ID) !== from);
        }
      } else if (o.op === "itinerary") {
        if (isPlaceholderDoc(document)) document = o.html;
      } else if (o.op === "link") {
        document = (document || "").split(`href="${o.from}"`).join(`href="${o.to}"`);
      } else if (o.op === "rename_guide") {
        attributes = { ...attributes, guide_name: o.name };
      } else if (o.op === "note") {
        setPlace(o.place_id, (p) => (String(p.Info || "").includes(noteHtml(o.text)) ? p : { ...p, Info: noteHtml(o.text) + (p.Info || "") }));
      } else if (o.op === "set") {
        setPlace(o.place_id, (p) => {
          if (o.set.Name && o.set.Name !== p.Name) {
            events = events.map((e) => (firstPlace(e) === String(p.ID) && e.Subject === p.Name ? { ...e, Subject: o.set.Name } : e));
          }
          return { ...p, ...o.set };
        });
      }
    })
  );
  return {
    ...data,
    ...(attributes ? { attributes } : {}),
    ...(document !== data.document ? { document } : {}),
    places: { ...data.places, placesList: places },
    ...(data.schedule ? { schedule: { ...data.schedule, events } } : {}),
  };
}

// The panel's line after Check (2026-11-02): a file already applied showed an empty panel with no word why.
export function planMessage(rows) {
  const ready = rows.filter((r) => r.status === "ready").length;
  const applied = rows.filter((r) => r.status === "done").length;
  if (ready) return "Pick a city.";
  if (applied === rows.length) return `Nothing to apply: all ${rows.length} fixes in this file are already applied.`;
  return `Nothing ready to apply (${applied} already applied, ${rows.length - applied} can't be applied — pick a city to see why).`;
}
